import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Calendar, Users, Link2, Video, ArrowRight, LogIn, Shield } from "lucide-react";
import { CrackedLogoFull } from "../components/CrackedLogo";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ADMIN_EMAIL = "vipin.singh@crack-ed.com";

export default function LandingPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(null);
  const [existingUser, setExistingUser] = useState(null);

  // Check for returning user from OAuth or existing session
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const userId = urlParams.get('user_id');
    const error = urlParams.get('error');

    if (error) {
      if (error === 'not_authorized_interviewer') {
        toast.error("You are not authorized to sign up as an interviewer. Please contact the administrator.");
      } else if (error === 'not_authorized_admin') {
        toast.error("Admin access is restricted.");
      } else {
        toast.error("Authentication failed. Please try again.");
      }
      // Clear the URL params
      window.history.replaceState({}, '', '/');
    }

    if (userId) {
      // Store user ID and redirect
      localStorage.setItem('cracked_user_id', userId);
      // Fetch user to determine redirect
      axios.get(`${API}/users/me?user_id=${userId}`).then(res => {
        if (res.data.role === 'admin') {
          navigate('/admin');
        } else if (res.data.role === 'interviewer') {
          navigate('/dashboard');
        } else {
          navigate('/book');
        }
      }).catch(() => {
        localStorage.removeItem('cracked_user_id');
      });
    } else {
      // Check if user already has a session
      const storedUserId = localStorage.getItem('cracked_user_id');
      if (storedUserId) {
        axios.get(`${API}/users/me?user_id=${storedUserId}`).then(res => {
          setExistingUser(res.data);
        }).catch(() => {
          localStorage.removeItem('cracked_user_id');
        });
      }
    }
  }, [navigate]);

  const handleGoogleLogin = async (role) => {
    setLoading(role);
    try {
      const response = await axios.get(`${API}/auth/google/login?role=${role}`);
      window.location.href = response.data.authorization_url;
    } catch (error) {
      toast.error("Failed to initiate login. Please try again.");
      setLoading(null);
    }
  };

  const handleSignIn = () => {
    // If user exists, go directly to their dashboard
    if (existingUser) {
      if (existingUser.role === 'admin') {
        navigate('/admin');
      } else if (existingUser.role === 'interviewer') {
        navigate('/dashboard');
      } else {
        navigate('/book');
      }
    } else {
      // Show sign-in with Google (will use existing auth if available)
      handleGoogleLogin('counsellor');
    }
  };

  const handleContinueAsUser = () => {
    if (existingUser) {
      if (existingUser.role === 'admin') {
        navigate('/admin');
      } else if (existingUser.role === 'interviewer') {
        navigate('/dashboard');
      } else {
        navigate('/book');
      }
    }
  };

  const features = [
    {
      icon: <Calendar className="w-6 h-6" />,
      title: "Smart Scheduling",
      description: "Set your availability and let candidates book slots that work for everyone"
    },
    {
      icon: <Users className="w-6 h-6" />,
      title: "Multi-Attendee Support",
      description: "Add candidate emails to automatically send calendar invites to all parties"
    },
    {
      icon: <Link2 className="w-6 h-6" />,
      title: "Shareable Links",
      description: "Generate unique booking links for counsellors to schedule interviews"
    },
    {
      icon: <Video className="w-6 h-6" />,
      title: "Auto Meet Links",
      description: "Google Meet links are automatically created and shared with all attendees"
    }
  ];

  return (
    <div className="min-h-screen bg-white relative overflow-hidden">
      {/* Decorative triangles */}
      <div className="triangle-decoration triangle-tl" />
      <div className="triangle-decoration triangle-br" />
      
      {/* Header */}
      <header className="relative z-10 px-6 py-4">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <CrackedLogoFull />
          {existingUser ? (
            <Button
              onClick={handleContinueAsUser}
              className="bg-cracked-light-teal hover:bg-cracked-deep-teal text-white"
              data-testid="continue-btn"
            >
              Continue as {existingUser.name?.split(' ')[0]}
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          ) : (
            <Button
              variant="outline"
              onClick={handleSignIn}
              className="border-cracked-light-teal text-cracked-light-teal hover:bg-cracked-light-teal hover:text-white"
              data-testid="signin-btn"
            >
              <LogIn className="w-4 h-4 mr-2" />
              Sign In
            </Button>
          )}
        </div>
      </header>

      {/* Hero Section */}
      <main className="relative z-10 px-6 pt-8 pb-24">
        <div className="max-w-6xl mx-auto">
          {/* Hero */}
          <div className="text-center mb-16 animate-fade-in">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-cracked-deep-teal mb-6 leading-tight">
              Interview Scheduling
              <br />
              <span className="text-cracked-light-teal">Made Simple</span>
            </h1>
            <p className="text-base lg:text-lg text-cracked-deep-teal/70 max-w-2xl mx-auto mb-10">
              Streamline your interview process. Connect your Google Calendar, 
              set availability, and let counsellors book slots effortlessly.
            </p>
          </div>

          {/* Role Selection Cards */}
          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto mb-20">
            {/* Interviewer Card */}
            <Card 
              data-testid="interviewer-signup-card"
              className="relative overflow-hidden border-2 border-transparent hover:border-cracked-light-teal transition-all duration-300 cursor-pointer group animate-fade-in stagger-1"
              onClick={() => handleGoogleLogin('interviewer')}
            >
              <div className="absolute top-0 right-0 w-24 h-24 bg-cracked-yellow/20 rounded-bl-full" />
              <CardHeader className="pb-4">
                <div className="w-14 h-14 rounded-xl bg-cracked-light-teal/10 flex items-center justify-center mb-4">
                  <Calendar className="w-7 h-7 text-cracked-light-teal" />
                </div>
                <CardTitle className="text-xl text-cracked-deep-teal">I'm an Interviewer</CardTitle>
                <CardDescription className="text-cracked-deep-teal/60">
                  Set your availability and share booking links
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 text-sm text-cracked-deep-teal/70 mb-6">
                  <li className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-cracked-light-teal" />
                    Configure your available time slots
                  </li>
                  <li className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-cracked-light-teal" />
                    Set custom slot durations (default 10 min)
                  </li>
                  <li className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-cracked-light-teal" />
                    Generate shareable booking links
                  </li>
                </ul>
                <Button 
                  data-testid="interviewer-signup-btn"
                  className="w-full bg-cracked-light-teal hover:bg-cracked-deep-teal text-white group-hover:translate-x-1 transition-transform"
                  disabled={loading === 'interviewer'}
                >
                  {loading === 'interviewer' ? (
                    <div className="spinner" />
                  ) : (
                    <>
                      Sign up as Interviewer
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>

            {/* Counsellor Card */}
            <Card 
              data-testid="counsellor-signup-card"
              className="relative overflow-hidden border-2 border-transparent hover:border-cracked-yellow transition-all duration-300 cursor-pointer group animate-fade-in stagger-2"
              onClick={() => handleGoogleLogin('counsellor')}
            >
              <div className="absolute top-0 right-0 w-24 h-24 bg-cracked-light-teal/10 rounded-bl-full" />
              <CardHeader className="pb-4">
                <div className="w-14 h-14 rounded-xl bg-cracked-yellow/20 flex items-center justify-center mb-4">
                  <Users className="w-7 h-7 text-cracked-yellow" />
                </div>
                <CardTitle className="text-xl text-cracked-deep-teal">I'm a Counsellor</CardTitle>
                <CardDescription className="text-cracked-deep-teal/60">
                  Book interview slots for candidates
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 text-sm text-cracked-deep-teal/70 mb-6">
                  <li className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-cracked-yellow" />
                    View available interviewers
                  </li>
                  <li className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-cracked-yellow" />
                    Select convenient time slots
                  </li>
                  <li className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-cracked-yellow" />
                    Add candidate emails to invites
                  </li>
                </ul>
                <Button 
                  data-testid="counsellor-signup-btn"
                  className="w-full bg-cracked-yellow hover:bg-cracked-orange text-cracked-deep-teal font-semibold group-hover:translate-x-1 transition-transform"
                  disabled={loading === 'counsellor'}
                >
                  {loading === 'counsellor' ? (
                    <div className="spinner" />
                  ) : (
                    <>
                      Sign up as Counsellor
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>
          </div>

          {/* Features Grid */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 max-w-5xl mx-auto">
            {features.map((feature, index) => (
              <div 
                key={index} 
                className={`text-center p-6 rounded-xl bg-cracked-deep-teal/5 animate-fade-in stagger-${index + 1}`}
              >
                <div className="w-12 h-12 rounded-lg bg-cracked-light-teal/10 flex items-center justify-center mx-auto mb-4 text-cracked-light-teal">
                  {feature.icon}
                </div>
                <h3 className="font-semibold text-cracked-deep-teal mb-2">{feature.title}</h3>
                <p className="text-sm text-cracked-deep-teal/60">{feature.description}</p>
              </div>
            ))}
          </div>

          {/* Admin Link */}
          <div className="text-center mt-12">
            <button
              onClick={() => handleGoogleLogin('admin')}
              className="text-sm text-cracked-deep-teal/40 hover:text-cracked-deep-teal/60 transition-colors inline-flex items-center gap-1"
              data-testid="admin-login-btn"
            >
              <Shield className="w-3 h-3" />
              Administrator Login
            </button>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="relative z-10 px-6 py-8 border-t border-cracked-deep-teal/10">
        <div className="max-w-6xl mx-auto text-center">
          <p className="text-sm text-cracked-deep-teal/50">
            © 2026 Crack-ED. Skill Up • Rise Up
          </p>
        </div>
      </footer>
    </div>
  );
}
